import { zodResolver } from '@hookform/resolvers/zod'
import { motion, AnimatePresence } from 'framer-motion'
import { Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react'
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import { paths } from 'src/config/paths'
import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'
import mapPinOnDisk from 'src/shared/assets/images/map-pin-on-disk.png'
import loanOfficer from 'src/shared/components/icons/professions/loan-officer.png'
import reAgent from 'src/shared/components/icons/professions/re-agent.png'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { cn } from 'src/shared/utils/cn'

import { expertiseMap } from '../../schemas/expertise'
import type { SearchFilters } from '../../schemas/marketplace/filters'
import { filtersSchema } from '../../schemas/marketplace/filters'
import type { Profession } from '../../schemas/profession'
import { professionMap } from '../../schemas/profession'
import useActiveFilterStore from '../../stores/marketplace/active-filter'
import { formatLocationFromUrl } from '../../utils/format-location'

import { useLocationInputRef } from './context/use-location-input'
import { useMarketplaceSearch } from './hooks/use-marketplace-search'
import filterPic from './icons/filter_selected.png'
import FiltersList from './search/filters-list'
import LocationInput from './search/location-input'
import LocationList from './search/location-list'
import ProfessionList from './search/profession-list'
import { handlePlaceDetails } from './util/get-place-details'

export interface SearchbarHandle {
  resetForm: () => void
}

type MarketplaceSearchbarProps = {
  className?: string
  searchButtonClassName?: string
  labelClassName?: string
  inputClassName?: string
}

export const PROFESSION_DATA = {
  real_estate_agent: {
    icon: reAgent,
  },
  mortgage_loan_officer: {
    icon: loanOfficer,
  },
}

const MarketplaceSearchbar = forwardRef<SearchbarHandle, MarketplaceSearchbarProps>(
  ({ className, searchButtonClassName, labelClassName, inputClassName }, ref) => {
    const {
      activeDropdown,
      setActiveDropdown,
      selectedProfession,
      setSelectedProfession,
      setSelectedLocation,
      setSubmittedFilters,
      submittedFilters,
      setIsUsingExactLocation,
      setLocationSearchInput,
      locationSearchInput,
      isUsingExactLocation,
      reset,
    } = useActiveFilterStore(
      useShallow((state) => ({
        activeDropdown: state.activeDropdown,
        setActiveDropdown: state.setActiveDropdown,
        selectedProfession: state.selectedProfession,
        setSelectedProfession: state.setSelectedProfession,
        setSelectedLocation: state.setSelectedLocation,
        setSubmittedFilters: state.setSubmittedFilters,
        submittedFilters: state.submittedFilters,
        setIsUsingExactLocation: state.setIsUsingExactLocation,
        setLocationSearchInput: state.setLocationSearchInput,
        locationSearchInput: state.locationSearchInput,
        isUsingExactLocation: state.isUsingExactLocation,
        reset: state.reset,
      })),
    )
    const searchBarRef = useRef<HTMLFormElement>(null)
    const locationInputRef = useLocationInputRef()
    const { data: mapApiKey } = useMapApiKey()
    const navigate = useNavigate()
    const { params } = useMarketplaceSearch()

    // Initialize form with URL params
    useEffect(() => {
      const { profession, city, state } = params

      if (profession) {
        setSelectedProfession(profession as Profession)
        form.setValue('profession', profession as Profession)
      }

      if (city && state) {
        const { city: formattedCity, state: formattedState, location } = formatLocationFromUrl(city, state)

        setLocationSearchInput(location)
        form.setValue('location', location)
        form.setValue('city', formattedCity)
        form.setValue('state', formattedState)
      }
    }, [])

    // useEffect(() => {
    //   if (locationInputRef.current) {
    //     locationInputRef.current.click()
    //     setTimeout(() => {
    //       locationInputRef.current?.blur()
    //       setActiveDropdown(null)
    //     }, 100)
    //   }
    // }, [])

    const onSubmit = useCallback(
      (data: SearchFilters) => {
        setSubmittedFilters(data)
        setActiveDropdown(null)
        if (!location.pathname.includes(paths.marketplace.path)) {
          navigate(paths.marketplace.getHref())
        }
      },
      [setSubmittedFilters, setActiveDropdown, navigate, location.pathname],
    )

    const form = useForm<SearchFilters>({
      defaultValues: submittedFilters,
      resolver: zodResolver(filtersSchema),
    })

    const { placesService, placePredictions, getPlacePredictions } = usePlacesAutocompleteService({
      apiKey: mapApiKey,
      debounce: 500,
    })

    const handlePlaceDetailsCallback = useCallback(
      (placeId: string) => {
        handlePlaceDetails({
          placesService,
          inputRef: locationInputRef,
          setSearchInput: setLocationSearchInput,
          setValue: form.setValue,
          setSelectedLocation,
        })(placeId)
      },
      [placesService, locationInputRef, setLocationSearchInput, form.setValue, setSelectedLocation],
    )

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setLocationSearchInput(value)
        if (value.length > 1) {
          getPlacePredictions({
            input: value,
            types: ['(cities)'],
            componentRestrictions: { country: 'us' },
          })
        }
        if (activeDropdown !== 'where') {
          setActiveDropdown('where')
        }
      },
      [getPlacePredictions, activeDropdown, setActiveDropdown, setLocationSearchInput],
    )

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
          setActiveDropdown(null)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [setActiveDropdown])

    const focusValue = form.watch('clientFocus')
    const expertiseValue = form.watch('expertise')
    const locationValue = form.watch('location')

    const expertiseActiveList = useMemo(() => {
      const activeFilters: string[] = []

      if (selectedProfession === 'real_estate_agent' && focusValue) {
        activeFilters.push(focusValue.charAt(0).toUpperCase() + focusValue.slice(1))
      }

      if (expertiseValue && selectedProfession) {
        expertiseValue.forEach((expertise) => {
          if (expertiseMap[selectedProfession] && expertiseMap[selectedProfession][expertise]) {
            activeFilters.push(expertiseMap[selectedProfession][expertise].label)
          }
        })
      }

      return activeFilters.join(', ')
    }, [selectedProfession, focusValue, expertiseValue])

    useImperativeHandle(ref, () => ({
      resetForm: () => {
        form.reset({})
        reset()
      },
    }))

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          ref={searchBarRef}
          className={cn(
            'relative mx-6 flex h-[65px] w-full max-w-[800px] items-center rounded-full border border-input/50 pr-[10px] transition-colors',
            activeDropdown ? 'bg-[#f6f7f7]' : 'bg-background',
            'hover:bg-[#f6f7f7]',
            className,
          )}
        >
          {/* Profession Search */}
          <div className="group/profession-search relative h-full min-w-0 flex-1">
            <div
              className={cn(
                'flex h-full cursor-pointer flex-col justify-center rounded-full bg-transparent pl-10 pr-4 transition-colors group-hover/profession-search:bg-white',
                activeDropdown === 'profession' && 'bg-white',
              )}
              onClick={() => {
                setActiveDropdown(activeDropdown === 'profession' ? null : 'profession')
              }}
            >
              {selectedProfession && (
                <img
                  src={PROFESSION_DATA[selectedProfession].icon || filterPic}
                  alt="Filter selected"
                  className="absolute left-5 top-1/2 hidden size-[43px] -translate-y-1/2 md:block"
                />
              )}
              <div className={cn('text-xs font-semibold text-muted', selectedProfession && 'md:ml-8', labelClassName)}>
                Start with
              </div>
              <div
                className={cn(
                  'truncate text-sm text-primary',
                  selectedProfession && 'font-semibold text-primary md:ml-8',
                  inputClassName,
                )}
              >
                {selectedProfession ? (
                  professionMap[selectedProfession].label
                ) : (
                  <>
                    Select{' '}
                    {activeDropdown === 'profession' && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="inline size-4"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    )}
                  </>
                )}
              </div>
            </div>
            <AnimatePresence>
              {activeDropdown === 'profession' && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-full z-10 mt-3 w-[400px] rounded-3xl border bg-background p-6 shadow-xl"
                >
                  <div className="flex flex-col gap-3">
                    <ProfessionList
                      selectedProfession={selectedProfession}
                      onSelectItem={(profession) => {
                        form.setValue('profession', profession)
                        setSelectedProfession(profession)
                        form.setValue('expertise', [])
                        form.setValue('clientFocus', undefined)
                        setActiveDropdown('where')
                        locationInputRef.current?.focus()
                      }}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {activeDropdown !== 'profession' && activeDropdown !== 'where' && <div className="h-8 w-px bg-input" />}

          {/* Where Search */}
          <div className="group/where-search relative h-full min-w-0 flex-1">
            <div
              className={cn(
                'flex h-full cursor-pointer flex-col justify-center rounded-full bg-transparent pl-7 pr-4 transition-colors group-hover/where-search:bg-white',
                activeDropdown === 'where' && 'bg-white',
              )}
              onClick={() => {
                setActiveDropdown(activeDropdown === 'where' ? null : 'where')
                locationInputRef.current?.focus()
              }}
            >
              {(isUsingExactLocation || locationValue) && (
                <img
                  src={mapPinOnDisk}
                  alt="Filter selected"
                  className="absolute left-[18px] top-1/2 hidden h-[38px] w-[27px] -translate-y-1/2 md:block"
                />
              )}
              <div
                className={cn(
                  'text-xs font-semibold text-muted',
                  (isUsingExactLocation || locationValue) && 'md:ml-8',
                  labelClassName,
                )}
              >
                Where
              </div>
              <LocationInput
                ref={locationInputRef}
                value={locationSearchInput}
                onChange={handleInputChange}
                className={cn(
                  'w-full border-none bg-transparent text-sm text-primary outline-none placeholder:text-primary',
                  (isUsingExactLocation || locationValue) && 'font-semibold text-primary md:ml-8',
                  inputClassName,
                )}
              />
            </div>
            <AnimatePresence>
              {activeDropdown === 'where' && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-full z-10 mt-3 w-[400px] rounded-3xl border bg-background p-8 shadow-xl"
                >
                  <LocationList
                    searchInput={locationSearchInput}
                    placePredictions={placePredictions}
                    onSelectItem={(placeId, description, isDefault) => {
                      handlePlaceDetailsCallback(placeId)
                      setActiveDropdown('filters')
                      setLocationSearchInput(description)

                      if (isDefault) {
                        getPlacePredictions({
                          input: description,
                          types: ['(cities)'],
                          componentRestrictions: { country: 'us' },
                        })
                      }
                      setIsUsingExactLocation(false)
                    }}
                    onSelectNearby={() => {
                      setActiveDropdown('filters')
                      setLocationSearchInput('Professionals near me')

                      setIsUsingExactLocation(true)
                      getPlacePredictions({
                        input: '',
                        types: ['(cities)'],
                        componentRestrictions: { country: 'us' },
                      })
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {activeDropdown !== 'where' && activeDropdown !== 'filters' && <div className="h-8 w-px bg-input" />}

          {/* Filters Search */}
          <div className="group/filters-search relative h-full min-w-0 flex-1">
            <div
              className={cn(
                'flex h-full cursor-pointer flex-col justify-center rounded-full bg-transparent pl-7 pr-2 transition-colors group-hover/filters-search:bg-white',
                activeDropdown === 'filters' && 'bg-white',
              )}
              onClick={() => {
                // Before we select filters, we need to select a profession first.
                if (selectedProfession === null) {
                  setActiveDropdown('profession')
                  return
                }
                setActiveDropdown(activeDropdown === 'filters' ? null : 'filters')
              }}
            >
              {/* {expertiseActiveList && (
                <img
                  src={filterPic}
                  alt="Filter selected"
                  className="absolute left-2 top-1/2 hidden size-[43px] -translate-y-1/2 md:block"
                />
              )} */}
              <div
                className={cn('text-xs font-semibold text-muted', /*expertiseActiveList && 'md:ml-8',*/ labelClassName)}
              >
                Expertise
              </div>
              <div
                className={cn(
                  'max-w-[270px] truncate text-sm text-primary',
                  expertiseActiveList && 'font-semibold text-primary',
                  inputClassName,
                )}
              >
                {expertiseActiveList || (
                  <>
                    Pick <span className="text-muted">(optional)</span>
                  </>
                )}
              </div>
            </div>
            <AnimatePresence>
              {activeDropdown === 'filters' && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-full z-10 mt-3 w-[400px] rounded-3xl border bg-background p-8 shadow-xl"
                >
                  <FiltersList selectedProfession={selectedProfession} control={form.control} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Search Button */}
          {selectedProfession && (isUsingExactLocation || locationValue) ? (
            <Button
              className={cn('ml-4 hidden items-center justify-center md:flex', searchButtonClassName)}
              style={{
                width: '132px',
                height: '55px',
                flexShrink: 0,
                borderRadius: '100px',
                background: 'var(--Secondary-Inverse, #45D88E)',
                color: 'var(--Primary, #1B3029)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '100%',
              }}
              onClick={form.handleSubmit(onSubmit)}
            >
              <Search className="mr-2 size-5" />
              Search
            </Button>
          ) : (
            <Button
              size="icon"
              className={cn('ml-4 size-12 rounded-full bg-primary hover:bg-primary/90', searchButtonClassName)}
              onClick={form.handleSubmit(onSubmit)}
            >
              <Search className="size-[25px]" />
            </Button>
          )}
        </form>
      </Form>
    )
  },
)

MarketplaceSearchbar.displayName = 'MarketplaceSearchbar'

export default MarketplaceSearchbar
